import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Clock, 
  ShoppingBag, 
  Users, 
  MapPin, 
  Globe, 
  Instagram, 
  ChevronRight, 
  LogOut, 
  ShieldCheck, 
  Sparkles,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrderContext } from '../../context/OrderContext';
import { useRestaurantContext } from '../../context/RestaurantContext';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import StoreStatusSheet from '../menu-page/components/StoreStatusSheet';
import { computeStoreStatus } from '../../utils/storeStatusUtils';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, isGuest, capturedEmail, signOutUser } = useAuth();
  const { orders } = useOrderContext();
  const { restaurant, theme } = useRestaurantContext();

  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const [isStoreStatusSheetOpen, setIsStoreStatusSheetOpen] = useState(false);

  const displayName = currentUser?.displayName || (isGuest ? 'Guest Customer' : 'Diner');
  const displayEmail = currentUser?.email || capturedEmail || (isGuest ? 'Guest Session' : 'No email linked');
  const photoUrl = currentUser?.photoURL;
  const userInitial = displayName.charAt(0).toUpperCase() || 'U';

  const restaurantName = restaurant?.name || 'Restaurant';
  const restaurantPhone = restaurant?.phone || '';
  const restaurantAddress = restaurant?.address || '';
  const restaurantWebsite = restaurant?.website || '';
  const instagramHandle = restaurant?.socialLinks?.instagram || '';

  const storeStatus = computeStoreStatus(restaurant);

  const handleSignOut = async () => {
    try {
      await signOutUser();
      setIsSignOutModalOpen(false);
      navigate('../', { relative: 'path' });
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* ─── Sticky Header ─────────────────────────────────────────────────── */}
      <header className="bg-white sticky top-0 z-30 shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-gray-100">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate('../', { relative: 'path' })}
              className="w-9 h-9 -ml-1 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors active:scale-95 cursor-pointer"
              title="Back to Menu"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-base font-black text-gray-900 tracking-tight">
              Profile & Account
            </h1>
          </div>

          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            {restaurantName}
          </span>
        </div>
      </header>

      {/* ─── Main Content ──────────────────────────────────────────────────── */}
      <main className="max-w-xl mx-auto p-4 space-y-4">
        {/* Profile Identity Card */}
        <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] relative overflow-hidden">
          {/* Subtle Background Accent */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-full -mr-10 -mt-10 pointer-events-none" />

          <div className="flex items-center gap-4 relative z-10">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow-sm shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gray-900 text-white flex items-center justify-center font-black text-2xl shadow-sm shrink-0">
                {userInitial}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h2 className="text-lg font-black text-gray-900 truncate leading-tight">
                  {displayName}
                </h2>
                {currentUser && !isGuest && (
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-bold">
                    <ShieldCheck size={11} />
                    Verified
                  </span>
                )}
                {isGuest && (
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">
                    Guest
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold mt-1 truncate">
                <Mail size={13} className="text-gray-400 shrink-0" />
                <span className="truncate">{displayEmail}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Activity & Quick Navigation ──────────────────────────────────── */}
        <div className="space-y-1.5">
          <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest px-1">
            Activity & History
          </h3>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden divide-y divide-gray-50">
            {/* Orders & Bills */}
            <button
              onClick={() => navigate('../orders', { relative: 'path' })}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <ShoppingBag size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 group-hover:text-black">
                    Orders & Bills
                  </h4>
                  <p className="text-[11px] text-gray-400 font-semibold">
                    View active orders, bills & past receipts
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {orders?.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">
                    {orders.length}
                  </span>
                )}
                <ChevronRight size={16} className="text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>

            {/* Queue Tokens & History */}
            <button
              onClick={() => navigate('../queue-history', { relative: 'path' })}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <Users size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 group-hover:text-black">
                    Queue History
                  </h4>
                  <p className="text-[11px] text-gray-400 font-semibold">
                    Past waitlist tokens and table status
                  </p>
                </div>
              </div>

              <ChevronRight size={16} className="text-gray-400 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>

        {/* ─── Restaurant Information & Links ───────────────────────────────── */}
        <div className="space-y-1.5">
          <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest px-1">
            Restaurant Details
          </h3>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden divide-y divide-gray-50">
            {/* Store Status & Hours */}
            <button
              onClick={() => setIsStoreStatusSheetOpen(true)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Clock size={16} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-900">Opening Hours & Schedule</h4>
                  <p className="text-[11px] text-gray-400 font-semibold">
                    {storeStatus?.isOpen ? 'Currently Open' : 'Currently Closed'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${storeStatus?.isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
                <ChevronRight size={16} className="text-gray-400" />
              </div>
            </button>

            {/* Call Restaurant */}
            {restaurantPhone && (
              <a
                href={`tel:${restaurantPhone}`}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    <Phone size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-900">Call Restaurant</h4>
                    <p className="text-[11px] text-gray-400 font-semibold">{restaurantPhone}</p>
                  </div>
                </div>

                <ChevronRight size={16} className="text-gray-400" />
              </a>
            )}

            {/* Address & Maps */}
            {restaurantAddress && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurantName + ' ' + restaurantAddress)}`}
                target="_blank"
                rel="noreferrer"
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    <MapPin size={16} />
                  </div>
                  <div className="max-w-[240px]">
                    <h4 className="text-xs font-bold text-gray-900">Location & Directions</h4>
                    <p className="text-[11px] text-gray-400 font-semibold truncate">{restaurantAddress}</p>
                  </div>
                </div>

                <ChevronRight size={16} className="text-gray-400" />
              </a>
            )}

            {/* Social Links / Website */}
            {(restaurantWebsite || instagramHandle) && (
              <a
                href={restaurantWebsite || `https://instagram.com/${instagramHandle}`}
                target="_blank"
                rel="noreferrer"
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center shrink-0">
                    {instagramHandle ? <Instagram size={16} /> : <Globe size={16} />}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-900">Online & Social</h4>
                    <p className="text-[11px] text-gray-400 font-semibold">
                      {instagramHandle ? `@${instagramHandle}` : 'Visit Website'}
                    </p>
                  </div>
                </div>

                <ChevronRight size={16} className="text-gray-400" />
              </a>
            )}
          </div>
        </div>

        {/* ─── Sign Out / Reset Session Button ──────────────────────────────── */}
        <div className="pt-2">
          <button
            onClick={() => setIsSignOutModalOpen(true)}
            className="w-full py-3.5 px-4 rounded-2xl bg-white border border-gray-200/80 hover:bg-red-50/50 hover:border-red-100 text-gray-700 hover:text-red-600 font-bold text-xs flex items-center justify-center gap-2 transition-all active:scale-[0.99] cursor-pointer shadow-2xs"
          >
            <LogOut size={16} />
            <span>{isGuest ? 'Clear Guest Session' : 'Sign Out'}</span>
          </button>
        </div>

        {/* App Footer */}
        <div className="text-center pt-4 pb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
          <span>Powered by Swadho Self-Order</span>
        </div>
      </main>

      {/* ─── Sign Out Confirmation Modal ────────────────────────────────────── */}
      {isSignOutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-xs w-full p-6 text-center space-y-4 shadow-2xl border border-gray-100">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto">
              <LogOut size={22} />
            </div>

            <div>
              <h3 className="text-base font-black text-gray-900">
                {isGuest ? 'Clear Guest Session?' : 'Sign Out?'}
              </h3>
              <p className="text-xs text-gray-500 font-semibold mt-1">
                You will be returned to the main menu page.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setIsSignOutModalOpen(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSignOut}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-xs font-bold shadow-sm hover:bg-red-700 cursor-pointer"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Timings Sheet */}
      <StoreStatusSheet
        isOpen={isStoreStatusSheetOpen}
        onClose={() => setIsStoreStatusSheetOpen(false)}
        restaurant={restaurant}
      />

      <BottomTabNavigation />
    </div>
  );
};

export default ProfilePage;
